import React, { useState, useRef, useEffect, useMemo } from 'react';

/**
 * Agnostic card-style single select with a searchable popover.
 *
 * Trigger renders as a card: avatar (derived initial + deterministic color) +
 * optional uppercase `label` + the selected option's label + chevron.
 * The popover holds a search field and a rich option list (avatar + label +
 * optional subtitle + check on the active one).
 *
 * Props:
 *  - value: selected option `{ value, label }` | null
 *  - onChange: (option) => void
 *  - options: Array<{ value, label }>
 *  - label?: small uppercase label shown in the trigger (e.g. "Projet")
 *  - placeholder?: trigger text when no value
 *  - searchPlaceholder?: search input placeholder
 *  - sectionLabel?: small header above the list
 *  - renderSubtitle?: (option) => node — optional per-option second line
 *  - getAvatarLabel?: (option) => string — text used to derive the avatar (defaults to option.label)
 */
const AVATAR_COLORS = [
  { bg: '#ece8fd', fg: '#5b3df0' }, // violet
  { bg: '#ffe9d6', fg: '#c2410c' }, // peach
  { bg: '#dcf5e6', fg: '#15784c' }, // green
  { bg: '#e0f0ff', fg: '#2f6df0' }, // blue
  { bg: '#fde8f0', fg: '#be185d' }, // pink
  { bg: '#fef3c7', fg: '#b45309' }, // amber
];

const colorFor = (str = '') => {
  const sum = [...str].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
};

const initialOf = (str = '') => (str.trim().charAt(0) || '?').toUpperCase();

const Avatar = ({ text, small = false }) => {
  const { bg, fg } = colorFor(text || '');
  const dim = small ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm';
  return (
    <span
      className={`inline-flex items-center justify-center rounded-lg font-bold flex-shrink-0 ${dim}`}
      style={{ background: bg, color: fg }}
      aria-hidden
    >
      {initialOf(text)}
    </span>
  );
};

const CardSelect = ({
  value,
  onChange,
  options = [],
  label = '',
  placeholder = 'Sélectionner...',
  searchPlaceholder = 'Rechercher...',
  sectionLabel,
  renderSubtitle,
  getAvatarLabel,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [open]);

  const avatarLabel = (opt) => (getAvatarLabel ? getAvatarLabel(opt) : opt?.label) || '';

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => (o.label || '').toLowerCase().includes(q));
  }, [options, query]);

  const handleSelect = (opt) => {
    onChange?.(opt);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex items-center gap-2.5 w-full rounded-xl border bg-white dark:bg-[#1e1e1e] px-2.5 py-2 text-left transition-colors ${
          open
            ? 'border-[#5b3df0] dark:border-[#a98ff5] ring-1 ring-[#5b3df0]/30'
            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
        }`}
      >
        {value ? (
          <Avatar text={avatarLabel(value)} />
        ) : (
          <span className="inline-flex w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-700 flex-shrink-0" aria-hidden />
        )}
        <span className="min-w-0 flex-1">
          {label && (
            <span className="block text-[10px] font-bold tracking-wider text-gray-400 dark:text-gray-500 leading-none mb-0.5">
              {label.toUpperCase()}
            </span>
          )}
          <span className={`block text-sm font-semibold truncate ${value ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`}>
            {value ? value.label : placeholder}
          </span>
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1.5 w-full min-w-[16rem] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1e1e1e] shadow-xl p-1.5"
          role="listbox"
        >
          <div className="relative mb-1.5">
            <svg
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#161616] pl-8 pr-2 py-1.5 text-sm text-gray-900 dark:text-white outline-none focus:border-[#5b3df0] dark:focus:border-[#a98ff5]"
            />
          </div>

          {sectionLabel && (
            <div className="px-2 pt-1 pb-1 text-[10px] font-bold tracking-wider text-gray-400 dark:text-gray-500">
              {sectionLabel.toUpperCase()}
            </div>
          )}

          <div className="max-h-72 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-2 py-3 text-sm text-gray-400 dark:text-gray-500 text-center">Aucun résultat</div>
            ) : (
              filtered.map((opt) => {
                const active = value && value.value === opt.value;
                const subtitle = renderSubtitle ? renderSubtitle(opt) : null;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => handleSelect(opt)}
                    className={`flex items-center gap-2.5 w-full rounded-lg px-2 py-1.5 text-left transition-colors ${
                      active ? 'bg-[#f3f1fe] dark:bg-[#2a2440]' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <Avatar text={avatarLabel(opt)} small />
                    <span className="min-w-0 flex-1">
                      <span className={`block text-sm font-semibold truncate ${active ? 'text-[#5b3df0] dark:text-[#a98ff5]' : 'text-gray-900 dark:text-white'}`}>
                        {opt.label}
                      </span>
                      {subtitle && (
                        <span className="block text-xs text-gray-400 dark:text-gray-500 truncate">{subtitle}</span>
                      )}
                    </span>
                    {active && (
                      <svg className="w-4 h-4 text-[#5b3df0] dark:text-[#a98ff5] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CardSelect;
